import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { api, Approval } from '../services/api';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(n: number | null | undefined) {
  if (!n) return '—';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0 });
}

// EMT replaces AGM in visible steps mapping
function getVisibleSteps(masqueradeRole: string | null): string[] | null {
  if (masqueradeRole === 'Director')    return ['Director'];
  if (masqueradeRole === 'EMT')         return ['EMT'];
  if (masqueradeRole === 'IT.Approver') return ['IT'];
  if (masqueradeRole === 'Staff')       return [];
  return null; // show all
}

type ApprovalAction = 'Approved' | 'Denied' | 'Revision' | 'Paused';

export function ApprovalsPage() {
  const navigate = useNavigate();
  const { user, masqueradeRole, isMasquerading } = useAuth();

  const [allApprovals, setAllApprovals] = useState<Approval[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [expanded, setExpanded]         = useState<number | null>(null);
  const [comments, setComments]         = useState<Record<number, string>>({});
  const [commentError, setCommentError] = useState<Record<number, string>>({});
  const [submitting, setSubmitting]     = useState<number | null>(null);
  const [toast, setToast]               = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const email = user?.email || '';

  useEffect(() => {
    if (!email) return;
    setLoading(true);
    api.approvals.pending(email)
      .then(data => {
        setAllApprovals(data);
        if (data.length > 0) setExpanded(data[0].ApprovalID);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [email]);

  const visibleSteps = getVisibleSteps(masqueradeRole);
  const approvals = visibleSteps === null
    ? allApprovals
    : allApprovals.filter(a => visibleSteps.includes(a.StepName));

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }

  async function handleAction(approval: Approval, action: ApprovalAction) {
    const comment = comments[approval.ApprovalID] || '';

    // Enforce mandatory comment for Denied and Revision
    if ((action === 'Denied' || action === 'Revision') && !comment.trim()) {
      setCommentError(prev => ({
        ...prev,
        [approval.ApprovalID]: `A comment is required when ${action === 'Denied' ? 'denying' : 'requesting revision'}.`,
      }));
      return;
    }

    setCommentError(prev => ({ ...prev, [approval.ApprovalID]: '' }));
    setSubmitting(approval.ApprovalID);

    try {
      const approverName = isMasquerading
        ? `${user?.name || email} (acting as ${masqueradeRole})`
        : user?.name || email;

      const result = await api.approvals.action(approval.ApprovalID, {
        action,
        comments:      comment || null,
        approverEmail: email,
        approverName,
      });

      showToast(
        `Project ${action.toLowerCase()} successfully. New status: ${result.newStatus}`,
        'success'
      );
      setAllApprovals(prev => prev.filter(a => a.ApprovalID !== approval.ApprovalID));
      setExpanded(null);
    } catch (e: any) {
      showToast(`Failed to process: ${e.message}`, 'error');
    } finally {
      setSubmitting(null);
    }
  }

  const priorityDot = (p: string) => {
    const cls = p === 'High' ? 'p-high' : p === 'Low' ? 'p-low' : 'p-normal';
    return <span className={`priority-dot ${cls}`} />;
  };

  if (masqueradeRole === 'Staff') {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Pending Review</h1>
            <p className="page-sub">Viewing as Staff</p>
          </div>
        </div>
        <div className="card">
          <div className="empty-state" style={{ padding: 48 }}>
            <div className="empty-state-icon">🔒</div>
            <div className="empty-state-title">No access</div>
            <div className="empty-state-sub">Staff members cannot approve or deny projects.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          padding: '12px 20px', borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: toast.type === 'success' ? 'var(--success-bg)' : 'var(--danger-bg)',
          color:      toast.type === 'success' ? 'var(--success-text)' : 'var(--danger-text)',
          border:     `1px solid ${toast.type === 'success' ? 'var(--success)' : 'var(--danger)'}`,
          boxShadow:  '0 4px 12px rgba(0,0,0,0.1)',
        }}>
          {toast.type === 'success' ? '✓ ' : '✗ '}{toast.message}
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Pending Review</h1>
          <p className="page-sub">
            {loading ? 'Loading...' : approvals.length === 0
              ? isMasquerading
                ? `No ${masqueradeRole} approvals pending`
                : "No pending approvals — you're all caught up"
              : `${approvals.length} project${approvals.length > 1 ? 's' : ''} awaiting ${isMasquerading ? masqueradeRole + ' ' : ''}approval`}
          </p>
        </div>
      </div>

      {/* Masquerade acting-as banner */}
      {isMasquerading && approvals.length > 0 && (
        <div style={{
          background: 'var(--info-bg, #eff6ff)', border: '1px solid var(--accent)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 16,
          fontSize: 13, color: 'var(--accent)',
        }}>
          👁️ Acting as <strong>{masqueradeRole}</strong> — approvals you make will be logged as <strong>{user?.name} (acting as {masqueradeRole})</strong> in the audit log.
        </div>
      )}

      {error && (
        <div style={{ padding: 16, color: 'var(--danger-text)', background: 'var(--danger-bg)', borderRadius: 8, marginBottom: 16 }}>
          Failed to load approvals: {error}
        </div>
      )}

      {loading && (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--text2)' }}>
          Loading pending approvals...
        </div>
      )}

      {!loading && !error && approvals.length === 0 && (
        <div className="card">
          <div className="empty-state" style={{ padding: 48 }}>
            <div className="empty-state-icon">✓</div>
            <div className="empty-state-title">All caught up</div>
            <div className="empty-state-sub">
              {isMasquerading
                ? `No projects are currently waiting for ${masqueradeRole} approval.`
                : 'No projects are currently waiting for your approval.'}
            </div>
          </div>
        </div>
      )}

      {/* Approval cards */}
      {!loading && !error && approvals.map(a => {
        const errMsg = commentError[a.ApprovalID] || '';
        const isSubmitting = submitting === a.ApprovalID;
        const isExpanded = expanded === a.ApprovalID;

        // Step badge color
        const stepCls = a.StepName === 'IT' ? 'badge-info'
          : a.StepName === 'EMT' ? 'badge-warning'
          : a.StepName === 'Director' ? 'badge-neutral'
          : 'badge-neutral';

        return (
          <div key={a.ApprovalID} className="card" style={{ marginBottom: 12, overflow: 'hidden' }}>
            <div
              style={{ padding: '14px 18px', display: 'flex', gap: 14, alignItems: 'flex-start', cursor: 'pointer' }}
              onClick={() => setExpanded(isExpanded ? null : a.ApprovalID)}
            >
              <div style={{ paddingTop: 2 }}>
                <span className={`badge ${stepCls}`}>{a.StepName}</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text1)' }}>{a.Title}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>Requested by {a.RequestorName}</span>
                  <span>·</span>
                  <span>{a.DepartmentName}</span>
                  {a.Priority && <>
                    <span>·</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      {priorityDot(a.Priority)} {a.Priority} priority
                    </span>
                  </>}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                {a.StartDate && <span style={{ fontSize: 12, color: 'var(--text2)' }}>{formatDate(a.StartDate)}</span>}
                <span style={{ color: 'var(--text3)' }}>{isExpanded ? '▾' : '▸'}</span>
              </div>
            </div>

            {isExpanded && (
              <div style={{ borderTop: '1px solid var(--border)', background: 'var(--surface2)' }}>
                {/* Detail grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
                  {[
                    ['Department',    a.DepartmentName],
                    ['Priority',      a.Priority || '—'],
                    ['Budget',        formatCurrency(a.EstimatedBudget ?? null)],
                    ['Timeline',      a.EstimatedTimeline || '—'],
                    ['Start Date',    a.StartDate ? formatDate(a.StartDate) : '—'],
                    ['End Date',      a.EndDate   ? formatDate(a.EndDate)   : '—'],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{k}</div>
                      <div style={{ fontSize: 13, color: 'var(--text1)', fontWeight: 500 }}>{v || '—'}</div>
                    </div>
                  ))}
                </div>

                {a.ProjectDescription && (
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Project Description</div>
                    <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.6 }}>{a.ProjectDescription}</div>
                  </div>
                )}

                <div style={{ padding: '14px 18px' }}>
                  <div style={{ marginBottom: 14 }}>
                    <label className="field-label">
                      Review Notes
                      <span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 11, marginLeft: 6 }}>
                        (required for Deny and Request Revision)
                      </span>
                    </label>
                    <textarea
                      className="field-textarea"
                      style={{ minHeight: 70, borderColor: errMsg ? 'var(--danger)' : undefined }}
                      placeholder="Add notes to accompany your decision..."
                      value={comments[a.ApprovalID] || ''}
                      onChange={e => {
                        setComments(c => ({ ...c, [a.ApprovalID]: e.target.value }));
                        if (errMsg) setCommentError(prev => ({ ...prev, [a.ApprovalID]: '' }));
                      }}
                    />
                    {errMsg && (
                      <div style={{ fontSize: 12, color: 'var(--danger-text, #991b1b)', marginTop: 4 }}>{errMsg}</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-success"
                      disabled={isSubmitting}
                      onClick={() => handleAction(a, 'Approved')}
                    >
                      {isSubmitting ? 'Processing...' : '✓ Approve'}
                    </button>
                    <button
                      className="btn"
                      disabled={isSubmitting}
                      onClick={() => handleAction(a, 'Revision')}
                      style={{ borderColor: 'var(--warning, #f59e0b)', color: 'var(--warning-text, #92400e)' }}
                    >
                      {isSubmitting ? 'Processing...' : '↩ Request Revision'}
                    </button>
                    <button
                      className="btn btn-danger"
                      disabled={isSubmitting}
                      onClick={() => handleAction(a, 'Denied')}
                    >
                      {isSubmitting ? 'Processing...' : '✗ Deny'}
                    </button>
                    <button
                      className="btn"
                      style={{ marginLeft: 'auto' }}
                      onClick={() => navigate(`/projects/${a.ProjectID}`)}
                    >
                      View Full Project →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
